import { useEffect, useRef } from "react";
import {
  APIProvider,
  useApiIsLoaded,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import TextInput from "../ui-lib/components/Inputs/TextInput";
import { notify } from "../ui-lib/components/Alerts/Toast";

type GooglePlacesInputProps = {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: any) => void;
  onValueChange?: (value: string) => void;
  onPlaceSelected?: (data: {
    place: any;
    formattedAddress?: string;
    latitude?: number;
    longitude?: number;
  }) => void;
  apiKey?: string;
  language?: string;
  disabled?: boolean;
};

const autocompleteOptions = {
  fields: [
    "address_components",
    "geometry",
    "icon",
    "name",
    "place_id",
    "formatted_address",
  ],
};

function GooglePlaceAutocompleteCore(props: Readonly<GooglePlacesInputProps>) {
  const {
    label,
    placeholder,
    value,
    onChange,
    onValueChange,
    onPlaceSelected,
    disabled,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const apiIsLoaded = useApiIsLoaded();
  const places = useMapsLibrary("places");

  useEffect(() => {
    if (!apiIsLoaded || !places || !inputRef.current) return;
    let listener: google.maps.MapsEventListener | undefined;
    try {
      const instance = new places.Autocomplete(
        inputRef.current,
        autocompleteOptions as google.maps.places.AutocompleteOptions,
      );
      listener = instance.addListener("place_changed", () => {
        const place = instance.getPlace();
        const formattedAddress =
          place?.formatted_address ?? inputRef.current?.value ?? "";
        const latitude = place?.geometry?.location?.lat?.();
        const longitude = place?.geometry?.location?.lng?.();
        onValueChange?.(formattedAddress);
        onPlaceSelected?.({
          place,
          formattedAddress,
          latitude,
          longitude,
        });
      });
    } catch (e: any) {
      console.error("Error initializing Google Places Autocomplete", e);
      notify({
        message: "Error loading Google Places Autocomplete",
        variant: "error",
      });
    }
    return () => {
      listener?.remove?.();
    };
  }, [places, apiIsLoaded]);

  return (
    <TextInput
      inputRef={inputRef}
      label={label}
      placeholder={placeholder}
      value={value ?? ""}
      disabled={disabled}
      onChange={onChange}
      onBlur={onChange}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          return false;
        }
      }}
    />
  );
}

export default function GooglePlaceAutocomplete(
  props: Readonly<GooglePlacesInputProps>,
) {
  // If no API key provided, just render the input without autocomplete
  if (!props.apiKey) {
    notify({ message: "Google API key is not provided", variant: "error" });
    return <GooglePlaceAutocompleteCore {...props} />;
  }
  return (
    <APIProvider
      apiKey={props.apiKey}
      language={props.language}
      libraries={["places"]}
    >
      <GooglePlaceAutocompleteCore {...props} />
    </APIProvider>
  );
}
